/**
 * StorefrontStoreList — per-store publish switch rows for the Item Detail page.
 *
 * Renders one row per store (color dot + store name + switch), driven by the
 * tenant's store registry — one store or ten, the list follows. Toggles call
 * the store-aware visibility API (`storefront_key` param) so only the chosen
 * store's row changes and its caches/search index are invalidated.
 *
 * Rendered by StorefrontSection for multi-store tenants; single-store
 * tenants keep the legacy single "Publish to Storefront" switch.
 *
 * Part of Epic 02 — Plan 205 (Multi-Storefront); revived + rewired in QF-899.
 */

import { useState } from "react";
import { cn } from "@/lib/utils";
import { Switch } from "@/components/ui/switch";
import { useStoreList, getStoreColor, type StorefrontEntry } from "@/hooks/useStoreList";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { getERPClient } from "@/lib/erp/client";
import { toast } from "sonner";
import { slugify } from "./storefrontUtils";

interface VisibilityRow {
  storefront_tenant: string;
  enabled: number;
  slug: string;
  short_description: string;
  name?: string;
}

interface StorefrontStoreListProps {
  itemName: string;
  formData: Record<string, any>;
  onFieldChange: (field: string, value: any) => void;
  isNew: boolean;
  rowPx: string;
  rowFirst: string;
  rowGap: string;
  rowLabel: string;
  dividerMx: string;
}

function getVisibilityRows(formData: Record<string, any>): VisibilityRow[] {
  const rows = formData?.custom_storefront_visibility;
  if (Array.isArray(rows)) return rows;
  return [];
}

function isStoreEnabled(rows: VisibilityRow[], storeKey: string): boolean {
  const row = rows.find((r) => r.storefront_tenant === storeKey);
  return row ? row.enabled === 1 : false;
}

function withRowToggled(
  rows: VisibilityRow[],
  storeKey: string,
  enabled: boolean,
  autoSlug: string,
): VisibilityRow[] {
  const idx = rows.findIndex((r) => r.storefront_tenant === storeKey);
  if (idx >= 0) {
    return rows.map((r, i) => (i === idx ? { ...r, enabled: enabled ? 1 : 0 } : r));
  }
  return [
    ...rows,
    { storefront_tenant: storeKey, enabled: enabled ? 1 : 0, slug: autoSlug, short_description: "" },
  ];
}

export function StorefrontStoreList({
  itemName,
  formData,
  onFieldChange,
  isNew,
  rowPx,
  rowFirst,
  rowGap,
  rowLabel,
  dividerMx,
}: StorefrontStoreListProps) {
  const { data: stores = [], isLoading } = useStoreList();
  const queryClient = useQueryClient();
  const [pendingKey, setPendingKey] = useState<string | null>(null);
  const visibilityRows = getVisibilityRows(formData);

  const { mutateAsync: toggleStore } = useMutation({
    mutationFn: async (params: { storefront_key: string; enabled: boolean; slug?: string }) => {
      const client = getERPClient();
      return client.call(
        "zaviago_backend.storefront.api_visibility_toggle.toggle_storefront_visibility",
        {
          item_code: itemName,
          enabled: params.enabled ? 1 : 0,
          storefront_key: params.storefront_key,
          ...(params.slug ? { slug: params.slug } : {}),
        },
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["list", "Item"] });
    },
  });

  if (isLoading) {
    return (
      <div className={cn(rowPx, "py-2 space-y-2")}>
        {[1, 2].map((i) => (
          <div key={i} className="h-[44px] rounded-md bg-muted/30 animate-pulse" />
        ))}
      </div>
    );
  }

  const handleToggle = async (store: StorefrontEntry) => {
    const next = !isStoreEnabled(visibilityRows, store.storefront_key);

    // Auto-slug when first enabling a store with no row yet
    let autoSlug = "";
    const existingRow = visibilityRows.find((r) => r.storefront_tenant === store.storefront_key);
    if (next && !existingRow?.slug) {
      const itemNameStr = (formData?.item_name as string) || "";
      if (itemNameStr) autoSlug = slugify(itemNameStr);
    }

    const newRows = withRowToggled(visibilityRows, store.storefront_key, next, autoSlug);
    const anyEnabled = newRows.some((r) => r.enabled === 1);

    // Optimistic update (rows + derived legacy bool keep the rest of the
    // section — dimming, catalog-only gating — in sync)
    onFieldChange("custom_storefront_visibility", newRows);
    onFieldChange("storefront_enabled", anyEnabled ? 1 : 0);

    if (isNew) return; // buffered — persisted with the create payload

    setPendingKey(store.storefront_key);
    try {
      await toggleStore({ storefront_key: store.storefront_key, enabled: next, slug: autoSlug || undefined });
    } catch {
      // Revert both
      onFieldChange("custom_storefront_visibility", visibilityRows);
      onFieldChange("storefront_enabled", visibilityRows.some((r) => r.enabled === 1) ? 1 : 0);
      toast.error("Failed to update store visibility. Please try again.");
    } finally {
      setPendingKey(null);
    }
  };

  return (
    <div>
      {stores.map((store, idx) => {
        const enabled = isStoreEnabled(visibilityRows, store.storefront_key);

        return (
          <div key={store.storefront_key}>
            <div className={cn(`flex items-center min-h-[44px] ${rowGap}`, rowPx, idx === 0 ? rowFirst : "")}>
              {/* Store color dot + name + domain */}
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <div className={cn("w-2 h-2 rounded-full shrink-0", getStoreColor(store.color_index))} />
                <div className="min-w-0">
                  <span className={cn(rowLabel, "block truncate text-gray-900 dark:text-gray-100")}>
                    {store.label}
                  </span>
                  <span className="block text-xs text-muted-foreground truncate">
                    {store.domain}
                  </span>
                </div>
              </div>

              {/* Switch */}
              <div onClick={(e) => e.stopPropagation()}>
                <Switch
                  checked={enabled}
                  onCheckedChange={() => {}}
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    handleToggle(store);
                  }}
                  disabled={pendingKey !== null}
                  aria-label={`Publish to ${store.label}`}
                />
              </div>
            </div>
            {idx < stores.length - 1 && (
              <div className={cn(dividerMx, "border-t border-border/40")} />
            )}
          </div>
        );
      })}
    </div>
  );
}
